import React, { useCallback, useState } from 'react';
import classes from '~/app/common/scss/list.module.scss';
import { useStoreActions, useStoreState } from '~/store';
import { Material, SaveMaterialResponse } from '~/types';
import useTranslation from '~/app/hooks/useTranslation';
import useAccessControl from '~/app/hooks/useAccessControl';
import { DEPARTMENT_PERMISSION_GROUPS } from '~/accessControl/permissionGroups';
import { PartLinkTypes } from '~/app/data/statuses/issue';
import MaterialRowMenu from '../MaterialRowMenu';
import MaterialForm from '../Add/MaterialForm';
import QuantityEditForm from '../QuantityEditForm';
import {
  formatMaterialPrice,
  getMaterialStoreKey,
  isMaterialsEditable,
  isVariousMaterial,
  toSaveMaterialPayload,
} from '../helper';

export interface MaterialsListProps {
  type: PartLinkTypes
}

export type ListComponent = React.FC<MaterialsListProps>;

const List: ListComponent = ({ type }) => {
  const basePath = getMaterialStoreKey(type);
  const materials = useStoreState(state => state[basePath].data.materials ?? []);
  const id = useStoreState(state => state[basePath].data.id);
  const status = useStoreState(state => state[basePath].data.status);
  const deptPrefix = useStoreState(state => state[basePath].data.departmentPrefix);
  const netWorkStatus = useStoreState(state => state.app.offline.status);

  const setTempMaterialsAction = useStoreActions(actions => actions[basePath].setTempMaterials);
  const saveMaterialsAction = useStoreActions(actions => actions[basePath].saveMaterials);
  const fetchMaterialsAction = useStoreActions(actions => actions[basePath].fetchMaterials);

  const [materialForEdit, setMaterialForEdit] = useState<Material>();
  const [isOpenMaterialForm, setIsOpenMaterialForm] = useState(false);
  const [isOpenQuantityEditForm, setIsOpenQuantityEditForm] = useState(false);

  const { t } = useTranslation('parts');
  const { checkPermission } = useAccessControl();

  const addMaterialPermission = deptPrefix
    ? checkPermission(DEPARTMENT_PERMISSION_GROUPS.addParts(deptPrefix))
    : false;

  const canEdit = !netWorkStatus && isMaterialsEditable(type, status) && addMaterialPermission;

  const persistMaterials = useCallback((nextMaterials: Material[]) => {
    setTempMaterialsAction(nextMaterials);
    return saveMaterialsAction(toSaveMaterialPayload(id, nextMaterials)).then((res: SaveMaterialResponse) => {
      if (res.success) {
        fetchMaterialsAction(String(id));
      }
      return res;
    });
  }, [id, setTempMaterialsAction, saveMaterialsAction, fetchMaterialsAction]);

  const onRemove = useCallback((materialToRemove: Material) => {
    const nextMaterials = materials.map(m =>
      String(m.id) === String(materialToRemove.id)
        ? { ...m, deleted: true, added: undefined }
        : { ...m, added: undefined }
    );
    persistMaterials(nextMaterials);
  }, [materials, persistMaterials]);

  const onEdit = useCallback((material: Material) => {
    setTempMaterialsAction(materials.map(m => ({ ...m, added: undefined })));
    setMaterialForEdit(material);
    if (isVariousMaterial(material)) {
      setIsOpenQuantityEditForm(true);
    } else {
      setIsOpenMaterialForm(true);
    }
  }, [materials, setTempMaterialsAction]);

  const closeMaterialForm = useCallback(() => {
    setIsOpenMaterialForm(false);
    setMaterialForEdit(undefined);
  }, []);

  const closeQuantityForm = useCallback(() => {
    setIsOpenQuantityEditForm(false);
    setMaterialForEdit(undefined);
  }, []);

  const onQuantitySave = useCallback((material: Material, quantity: number) => {
    const nextMaterials = materials.map(m =>
      String(m.id) === String(material.id)
        ? { ...m, quantity, added: undefined }
        : { ...m, added: undefined }
    );
    persistMaterials(nextMaterials);
  }, [materials, persistMaterials]);

  return (
    <div>
      {materials.map((material) => {
        const displayName = isVariousMaterial(material)
          ? t('variousMaterial')
          : (material.name ?? '');
        return (
          <div className={classes['partBox']} key={String(material.id)}>
            <div className={classes['row']}>
              <span className={classes['partName']}>{displayName}</span>
            </div>
            <div className={classes['row']}>
              <span className={classes['partNumber']}>
                {material.categoryName
                  ? `${material.categoryName} · `
                  : ''}
                {formatMaterialPrice(Number(material.pricePerUnit) || 0)}
                {' · '}
                {material.quantity} {t('item')}
              </span>
              {canEdit && (
                <MaterialRowMenu
                  material={material}
                  onEdit={onEdit}
                  onRemove={onRemove}
                />
              )}
            </div>
          </div>
        );
      })}
      {canEdit && (
        <>
          <MaterialForm
            open={isOpenMaterialForm}
            onClose={closeMaterialForm}
            material={materialForEdit}
            type={type}
            persistOnSave
          />
          <QuantityEditForm
            open={isOpenQuantityEditForm}
            onClose={closeQuantityForm}
            material={materialForEdit}
            onSave={onQuantitySave}
          />
        </>
      )}
    </div>
  );
};

export default List;
